import Constants from 'expo-constants';

const extra = (Constants.expoConfig?.extra ?? {}) as {
  revenueCatAndroidApiKey?: string;
  revenueCatIosApiKey?: string;
  revenueCatWebApiKey?: string;
};

/**
 * RevenueCat configuration.
 *
 * Set API keys via environment variables (recommended) or Expo extra in app.config.js:
 *   EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY
 *   EXPO_PUBLIC_REVENUECAT_IOS_API_KEY
 *   EXPO_PUBLIC_REVENUECAT_WEB_API_KEY
 *
 * In RevenueCat dashboard, create entitlements `standard` and `premium`, then attach
 * your store products (standard_monthly, standard_yearly, premium_monthly, premium_yearly).
 */
export const REVENUECAT_CONFIG = {
  apiKeyAndroid:
    process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY ??
    extra.revenueCatAndroidApiKey ??
    '',
  apiKeyIOS:
    process.env.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY ??
    extra.revenueCatIosApiKey ??
    '',
  apiKeyWeb:
    process.env.EXPO_PUBLIC_REVENUECAT_WEB_API_KEY ??
    extra.revenueCatWebApiKey ??
    '',
  entitlements: {
    standard: 'standard',
    premium: 'premium',
  },
  offeringId: 'default',
} as const;

/** Store product identifiers — must match Google Play / App Store and RevenueCat products */
export const SUBSCRIPTION_PRODUCT_IDS = [
  'standard_monthly',
  'standard_yearly',
  'premium_monthly',
  'premium_yearly',
] as const;

/** App Store Connect subscription group ID (numeric). Optional — resolved from StoreKit if omitted. */
export const IOS_SUBSCRIPTION_GROUP_ID =
  process.env.EXPO_PUBLIC_IOS_SUBSCRIPTION_GROUP_ID ?? '';

export type SubscriptionProductId = (typeof SUBSCRIPTION_PRODUCT_IDS)[number];

export const SUBSCRIPTION_PRODUCTS = SUBSCRIPTION_PRODUCT_IDS.map((id) => ({
  id,
  tier: id.includes('premium') ? 'premium' : 'standard',
  billingPeriod: id.includes('yearly') ? 'yearly' : 'monthly',
})) as Array<{
  id: SubscriptionProductId;
  tier: 'standard' | 'premium';
  billingPeriod: 'monthly' | 'yearly';
}>;

export const isRevenueCatConfigured = (): boolean => {
  const { apiKeyAndroid, apiKeyIOS, apiKeyWeb } = REVENUECAT_CONFIG;
  return Boolean(apiKeyAndroid || apiKeyIOS || apiKeyWeb);
};

export const isRevenueCatWebConfigured = (): boolean =>
  Boolean(REVENUECAT_CONFIG.apiKeyWeb);
